import { PayloadAction } from '@reduxjs/toolkit';
import { createSliceWithStateReset } from 'store/createSliceWithStateReset';
import { RootState } from 'store/store';
import {
  EligibilityCheckMethod,
  SignupCaregiverRelationship,
  SignupFormData,
  SignupAlertError,
  SignupDialogError,
  Policy,
  SignupStep,
  SignUpUserType,
  SignupUserStatus,
} from 'features/signUp/types';
import { FeatureFlag, OptInMessage } from 'features/auth/types';

export interface SSOAuthConfig {
  integrationId: string;
  authUserId: string;
}

export interface SignUpFlowState {
  currentStep: SignupStep;
  stepStack: SignupStep[];
  userType: SignUpUserType;
  showExitDialog: boolean;
  isShowingAlertError: boolean;
  alertError: SignupAlertError;
  isShowingDialogError: boolean;
  dialogError: SignupDialogError;
  wellframeUserId?: number;
  memberId?: string;
  activeUserToken?: string;
  hospitalID: string;
  hospitalDisplayName?: string;
  isUserInvitedCaregiver?: boolean;
  isHospitalCaregiverEnabled?: boolean;
  isEligibilityCheckRequired?: boolean;
  eligibilityCheckMethod?: EligibilityCheckMethod;
  eligibilityFieldPhoto?: string;
  eligibilityMessage?: string;
  isHospitalSsoEnabled?: boolean;
  caregiverRelationships?: SignupCaregiverRelationship[];
  privacyPolicy?: Policy;
  eulaPolicy?: Policy;
  isUserApproved?: boolean;
  userSignupStatus?: SignupUserStatus;
  formData: SignupFormData;
  signUpNotificationPref?: {
    retryFailed?: boolean;
    smsNotificationOptInEnabled: boolean;
  };
  notificationFlag?: FeatureFlag;
  ssoAuthConfig?: SSOAuthConfig;
  isHospitalSmsEnabled?: boolean;
  optInMessage?: OptInMessage;
}

const initialState: SignUpFlowState = {
  showExitDialog: false,
  isShowingAlertError: false,
  alertError: {
    message: '',
    position: 'top',
    handleDismiss: () => {},
  },
  isShowingDialogError: false,
  dialogError: {
    title: '',
    body: '',
    helpElement: { type: 'button', label: '', onClick: () => {} },
    primaryCtaButton: {
      label: '',
      onClick: () => {},
    },
    onClose: () => {},
    type: null,
  },
  hospitalID: '',
  currentStep: 'AccessCodeSubmission',
  stepStack: ['AccessCodeSubmission'],
  userType: 'member', // Default user type, can be overridden by the user during the flow
  formData: {
    accessCode: '',
    memberId: '',
    memberFirstName: '',
    memberLastName: '',
    memberDateOfBirth: '',
    memberStateOfResidence: '',
    memberGender: '',
    memberPhoneNumber: '',
    memberEmail: '',
    memberConfirmedEmail: '',
    password: '',
    confirmedPassword: '',
    smsOptIn: false,
    privacyPolicyAccepted: false,
    eulaPolicyAccepted: false,
  },
};

const signUpSlice = createSliceWithStateReset({
  name: 'signUp',
  initialState,
  reducers: {
    resetSignUpState: () => initialState,
    openExitDialog: (state) => ({
      ...state,
      showExitDialog: true,
    }),
    closeExitDialog: (state) => ({
      ...state,
      showExitDialog: false,
    }),
    showAlertError: (state, action: PayloadAction<SignupAlertError>) => ({
      ...state,
      isShowingAlertError: true,
      alertError: action.payload,
    }),
    hideAlertError: (state) => ({
      ...state,
      isShowingAlertError: false,
    }),
    showDialogError: (state, action: PayloadAction<SignupDialogError>) => ({
      ...state,
      isShowingDialogError: true,
      dialogError: action.payload,
    }),
    hideDialogError: (state) => ({
      ...state,
      isShowingDialogError: false,
    }),
    setSignUpNotificationPref: (
      state,
      action: PayloadAction<{ retryFailed?: boolean; smsNotificationOptInEnabled: boolean }>,
    ) => ({
      ...state,
      signUpNotificationPref: action.payload,
    }),
    setStepStack: (state, action: PayloadAction<SignupStep[]>) => ({
      ...state,
      stepStack: action.payload,
    }),
    setUserType: (state, action: PayloadAction<SignUpUserType>) => ({
      ...state,
      userType: action.payload,
    }),
    setNotificationFlag: (state, action: PayloadAction<FeatureFlag>) => ({
      ...state,
      notificationFlag: action.payload,
    }),
    setSignUpState: (state, action: PayloadAction<Partial<SignUpFlowState>>) => ({
      ...state,
      ...action.payload,
    }),
  },
});

export const {
  openExitDialog,
  closeExitDialog,
  setStepStack,
  setUserType,
  setSignUpState,
  resetSignUpState,
  showAlertError,
  hideAlertError,
  showDialogError,
  hideDialogError,
  setSignUpNotificationPref,
  setNotificationFlag,
} = signUpSlice.actions;
export const { reducer: signUpReducer } = signUpSlice;
export const selectSignUpState = (state: RootState): SignUpFlowState => ({
  currentStep: state.signUp.stepStack[state.signUp.stepStack.length - 1], // derive current step from stepStack
  stepStack: state.signUp.stepStack,
  wellframeUserId: state.signUp.wellframeUserId,
  activeUserToken: state.signUp.activeUserToken,
  showExitDialog: state.signUp.showExitDialog,
  isShowingAlertError: state.signUp.isShowingAlertError,
  alertError: state.signUp.alertError,
  isShowingDialogError: state.signUp.isShowingDialogError,
  dialogError: state.signUp.dialogError,
  hospitalID: state.signUp.hospitalID,
  userType: state.signUp.userType,
  isUserInvitedCaregiver: state.signUp.isUserInvitedCaregiver,
  isHospitalCaregiverEnabled: state.signUp.isHospitalCaregiverEnabled,
  isEligibilityCheckRequired: state.signUp.isEligibilityCheckRequired,
  eligibilityCheckMethod: state.signUp.eligibilityCheckMethod,
  isHospitalSsoEnabled: state.signUp.isHospitalSsoEnabled,
  caregiverRelationships: state.signUp.caregiverRelationships,
  privacyPolicy: state.signUp.privacyPolicy,
  eulaPolicy: state.signUp.eulaPolicy,
  isUserApproved: state.signUp.isUserApproved,
  userSignupStatus: state.signUp.userSignupStatus,
  formData: state.signUp.formData,
  signUpNotificationPref: state.signUp.signUpNotificationPref,
  eligibilityFieldPhoto: state.signUp.eligibilityFieldPhoto,
  eligibilityMessage: state.signUp.eligibilityMessage,
  ssoAuthConfig: state.signUp.ssoAuthConfig,
  notificationFlag: state.signUp.notificationFlag,
  isHospitalSmsEnabled: state.signUp.isHospitalSmsEnabled,
  optInMessage: state.signUp.optInMessage,
});
